/**
 * BYOB BUILDER  v0.1  (2026-08-24)
 * ---------------------------------------------------------------------------
 * Client for the Build Your Own Basket flow.
 *
 * THE CENTRAL IDEA: THE URL IS THE QUERY
 *   Liquid cannot read arbitrary query parameters, so this client never posts
 *   filter state anywhere. It builds a Shopify URL and renders whatever comes
 *   back from sections/bb-builder-grid.liquid:
 *
 *     a filter chip   ->  /collections/<handle>?section_id=bb-builder-grid
 *     a search        ->  /search?q=<terms>&type=product&section_id=...
 *     page 2          ->  &page=2
 *     a sort          ->  &sort_by=price-ascending
 *
 *   Pagination, sorting and search are therefore Shopify's, not ours.
 *
 * THE "ALL" VIEW
 *   A step has several source collections (drinks = alcohol + mixers, plus 13
 *   subset chips). "All" walks them in order, exhausting one before starting
 *   the next, de-duplicating by product id. Overlap between a parent and its
 *   subsets is expected and handled — see BYOB_BUILDER_SPEC.md.
 *
 * NOT IN THIS COMMIT
 *   Selection state, multi-box, add-to-cart, summary. `onAdd` is a stub.
 *
 * DEBUG
 *   window.__bbBuilder.state()   current step, chip, query, paging cursor
 *   window.__bbBuilderVerbose = true
 */
(function () {
  'use strict';

  var LOG = '[bb-builder]';
  var GRID_SECTION = 'bb-builder-grid';
  var SEARCH_DEBOUNCE_MS = 300;

  var root = document.querySelector('[data-bb-builder]');
  if (!root) return;

  var configNode = document.querySelector('script[data-bb-builder-config]');
  if (!configNode) {
    console.warn(LOG, 'config node missing; builder inert');
    return;
  }

  var CONFIG;
  try {
    CONFIG = JSON.parse(configNode.textContent);
  } catch (e) {
    console.warn(LOG, 'config unparseable; builder inert', e);
    return;
  }

  if (!CONFIG.steps || !CONFIG.steps.length) {
    console.warn(LOG, 'config has no steps; builder inert');
    return;
  }

  var els = {
    steps: root.querySelector('[data-bb-steps]'),
    search: root.querySelector('[data-bb-search]'),
    status: root.querySelector('[data-bb-status]'),
    mount: root.querySelector('[data-bb-grid-mount]'),
    loadMore: root.querySelector('[data-bb-load-more]'),
    next: root.querySelector('[data-bb-next]'),
    filtersOpen: root.querySelector('[data-bb-filters-open]'),
    filterCount: root.querySelector('[data-bb-filter-count]'),
    filterGroups: root.querySelector('[data-bb-filter-groups]'),
    drawer: root.querySelector('[data-bb-drawer]'),
    box: root.querySelector('[data-bb-box]'),
    boxEmpty: root.querySelector('[data-bb-box-empty]'),
    boxCount: root.querySelector('[data-bb-box-count]'),
    boxTotal: root.querySelector('[data-bb-box-total]'),
    boxLabel: root.querySelector('[data-bb-box-label]'),
    addBox: root.querySelector('[data-bb-add-box]'),
    message: root.querySelector('[data-bb-message]'),
    msgTo: root.querySelector('[data-bb-msg-to]'),
    msgBody: root.querySelector('[data-bb-msg-body]'),
    msgFrom: root.querySelector('[data-bb-msg-from]'),
    msgRemaining: root.querySelector('[data-bb-msg-remaining]'),
    msgFields: root.querySelector('[data-bb-msg-fields]'),
    cards: root.querySelector('[data-bb-cards]'),
    back: root.querySelector('[data-bb-back]'),
    qv: root.querySelector('[data-bb-qv]'),
    qvBody: root.querySelector('[data-bb-qv-body]'),
    summary: root.querySelector('[data-bb-summary-panel]')
  };

  /**
   * Display only. The adult signature is computed server-side by
   * bb-cart-fee-state.liquid from the real cart, which is authoritative. This
   * is shown as a note OUTSIDE the subtotal so the two can never disagree on
   * screen — if the reconciler's rules change, the worst case here is a stale
   * note, not a wrong total.
   */
  var SIGNATURE_FEE = 699;

  var qvCache = Object.create(null);   // handle -> product json
  var qvReturnFocus = null;            // element to restore focus to on close
  var qvCard = null;                   // the card the quick view was opened from

  var STEP_ICON = '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">' +
    '<rect x="3" y="6" width="14" height="11" rx="1"/><path d="M3 9h14M10 6v11"/></svg>';

  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // ── State ─────────────────────────────────────────────────────────────────
  var state = {
    stepIndex: 0,
    filters: [],         // checked collection handles; [] = unfiltered
    query: '',           // search terms, or '' for none
    sourceIndex: 0,      // which source collection "All" is currently walking
    page: 1,
    hasNext: false,
    seen: Object.create(null),  // product id -> true, de-dupes across sources
    loading: false,
    token: 0,            // increments per navigation; stale responses discarded
    box: [],             // [{variantId, ..., boxId, step, qty}]
    boxId: 1,            // the box currently being filled
    card: null,          // {variantId, title, price} — greeting card variant
    submitting: false
  };

  function log() {
    if (!window.__bbBuilderVerbose) return;
    try { console.log.apply(console, [LOG].concat([].slice.call(arguments))); } catch (e) {}
  }

  function currentStep() {
    return CONFIG.steps[state.stepIndex];
  }

  /**
   * What "All" walks. Ticking Whiskey and Bourbon shows the union of both, so
   * a multi-select filter is just a different source list — the same
   * concatenate-and-de-duplicate walk handles it with no special casing.
   */
  function activeSources() {
    if (state.filters.length) {
      return state.filters.map(function (h) { return { handle: h }; });
    }
    return currentStep().sources;
  }

  // ── URL construction ──────────────────────────────────────────────────────
  function buildUrl() {
    var root_ = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var url;

    if (state.query) {
      url = new URL(root_ + 'search', window.location.origin);
      url.searchParams.set('q', state.query);
      url.searchParams.set('type', 'product');
    } else {
      var src = activeSources()[state.sourceIndex];
      if (!src) return null;
      url = new URL(root_ + 'collections/' + src.handle, window.location.origin);
    }

    url.searchParams.set('section_id', GRID_SECTION);
    if (state.page > 1) url.searchParams.set('page', String(state.page));
    return url.toString();
  }

  // ── Fetching ──────────────────────────────────────────────────────────────
  function fetchPage() {
    var url = buildUrl();
    if (!url) return Promise.resolve(null);

    return fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (res) {
        if (!res.ok) throw new Error('grid fetch ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var metaNode = doc.querySelector('[data-bb-grid-meta]');
        var items = doc.querySelectorAll('[data-bb-product]');
        var meta = null;
        try {
          meta = metaNode ? JSON.parse(metaNode.textContent) : null;
        } catch (e) {
          log('meta unparseable', e);
        }
        return { meta: meta, items: items };
      });
  }

  /**
   * Append a batch, skipping products already shown. De-duplication matters
   * because "All" walks overlapping collections — a whiskey is in both
   * `alcohol` and `whiskey`.
   */
  function appendItems(nodes) {
    var list = els.mount.querySelector('[data-bb-grid]');
    if (!list) {
      list = document.createElement('ul');
      list.className = 'bb-grid';
      list.setAttribute('data-bb-grid', '');
      list.setAttribute('role', 'list');
      els.mount.appendChild(list);
    }

    var added = 0;
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      var id = node.getAttribute('data-product-id');
      if (!id || state.seen[id]) continue;
      state.seen[id] = true;
      var card = document.importNode(node, true);
      var qty = document.createElement('div');
      qty.className = 'bb-grid__qty';
      qty.innerHTML =
        '<button type="button" data-bb-qty="-1" aria-label="Remove one">&minus;</button>' +
        '<output data-bb-qty-value>1</output>' +
        '<button type="button" data-bb-qty="1" aria-label="Add one">+</button>';
      card.appendChild(qty);
      list.appendChild(card);
      added++;
    }
    return added;
  }

  /**
   * Load one page. When "All" exhausts a source collection it advances to the
   * next one rather than stopping, so the step reads as a single list.
   */
  function loadMore() {
    if (state.loading) return Promise.resolve();
    state.loading = true;
    var token = state.token;
    setStatus('Loading…');

    return fetchPage()
      .then(function (result) {
        if (token !== state.token) return;      // navigated away mid-flight
        if (!result) { state.hasNext = false; return; }

        var added = appendItems(result.items);
        var meta = result.meta || {};

        if (meta.has_next) {
          state.page = meta.next_page;
          state.hasNext = true;
        } else if (!state.query) {
          // Source exhausted — move to the next source in this step.
          state.sourceIndex++;
          state.page = 1;
          state.hasNext = state.sourceIndex < activeSources().length;
        } else {
          state.hasNext = false;
        }

        log('page loaded', { added: added, meta: meta, hasNext: state.hasNext });

        // A page can be entirely duplicates when walking overlapping sources.
        // Pull the next one rather than showing the customer a dead button.
        if (added === 0 && state.hasNext) {
          state.loading = false;
          return loadMore();
        }
      })
      .catch(function (err) {
        if (token !== state.token) return;
        console.warn(LOG, 'load failed', err);
        setStatus('Could not load products. Try again.');
      })
      .then(function () {
        if (token !== state.token) return;
        state.loading = false;
        els.loadMore.hidden = !state.hasNext;
        var count = els.mount.querySelectorAll('[data-bb-product]').length;
        setStatus(count ? count + ' product' + (count === 1 ? '' : 's') : 'Nothing found.');
      });
  }

  function setStatus(text) {
    if (els.status) els.status.textContent = text;
  }

  // ── Navigation ────────────────────────────────────────────────────────────
  function reset() {
    state.token++;
    state.sourceIndex = 0;
    state.page = 1;
    state.hasNext = false;
    state.seen = Object.create(null);
    state.loading = false;
    els.mount.innerHTML = '';
    els.loadMore.hidden = true;
  }

  function navigate() {
    reset();
    return loadMore();
  }

  /**
   * The section emits an empty <nav>; steps come from the same config object
   * the chips do. Rendering both here keeps one source of truth and avoids
   * the cross-scope problem that made v0.2 emit "steps": [] in production.
   */
  function renderSteps() {
    els.steps.innerHTML = CONFIG.steps.map(function (st, i) {
      var cls = 'bb-step';
      if (i === state.stepIndex) cls += ' is-active';
      else if (i < state.stepIndex) cls += ' is-complete';
      var locked = i > state.stepIndex && !stepSatisfied(state.stepIndex);
      return '<button type="button" class="' + cls + '" data-bb-step="' + st.key + '"' +
             (locked ? ' disabled' : '') +
             ' aria-current="' + (i === state.stepIndex ? 'step' : 'false') + '">' +
             '<span class="bb-step__dot">' + STEP_ICON + '</span>' +
             '<span class="bb-step__name">' + escapeHtml(st.name) + '</span>' +
             '</button>';
    }).join('');
  }

  /** A step is satisfied once the box holds at least its minimum. */
  function stepSatisfied(index) {
    var st = CONFIG.steps[index];
    if (!st || !st.min) return true;
    return boxCountForStep(index) >= st.min;
  }

  function boxCountForStep(index) {
    var key = CONFIG.steps[index].key;
    return currentBoxItems().reduce(function (n, item) {
      return item.step === key ? n + item.qty : n;
    }, 0);
  }

  function renderFilters() {
    var groups = currentStep().filterGroups || [];
    if (!groups.length) {
      els.filtersOpen.hidden = true;
      els.filterGroups.innerHTML = '';
      return;
    }
    els.filtersOpen.hidden = false;

    els.filterGroups.innerHTML = groups.map(function (g, gi) {
      var opts = g.options.map(function (o) {
        var checked = state.filters.indexOf(o.handle) !== -1;
        return '<label class="bb-fopt">' +
               '<input type="checkbox" data-bb-filter="' + o.handle + '"' + (checked ? ' checked' : '') + '>' +
               '<span>' + escapeHtml(o.title) + '</span>' +
               '<span class="bb-fopt__count">' + o.count + '</span>' +
               '</label>';
      }).join('');

      return '<div class="bb-fgroup' + (gi === 0 ? ' is-open' : '') + '" data-bb-fgroup>' +
             '<button type="button" class="bb-fgroup__head" data-bb-fgroup-toggle>' +
             '<span>' + escapeHtml(g.label) + '</span>' +
             '<svg class="bb-fgroup__chevron" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="m3 5 4 4 4-4" stroke-linecap="round"/></svg>' +
             '</button>' +
             '<div class="bb-fgroup__body">' + opts + '</div>' +
             '</div>';
    }).join('');

    updateFilterCount();
  }

  function updateFilterCount() {
    var n = state.filters.length;
    els.filterCount.hidden = n === 0;
    els.filterCount.textContent = String(n);
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // ── Box ───────────────────────────────────────────────────────────────────
  function findInBox(variantId) {
    for (var i = 0; i < state.box.length; i++) {
      if (state.box[i].variantId === variantId && state.box[i].boxId === state.boxId) {
        return state.box[i];
      }
    }
    return null;
  }

  /** Items in the box currently being filled. Earlier boxes stay untouched. */
  function currentBoxItems() {
    return state.box.filter(function (b) { return b.boxId === state.boxId; });
  }

  function addToBox(card) {
    var variantId = card.getAttribute('data-variant-id');
    var existing = findInBox(variantId);
    if (existing) {
      existing.qty++;
    } else {
      var img = card.querySelector('img');
      state.box.push({
        variantId: variantId,
        productId: card.getAttribute('data-product-id'),
        title: card.getAttribute('data-title'),
        price: parseInt(card.getAttribute('data-price'), 10) || 0,
        image: img ? img.currentSrc || img.src : '',
        step: currentStep().key,
        boxId: state.boxId,
        alcohol: card.getAttribute('data-alcohol') === 'true',
        qty: 1
      });
    }
    renderBox();
    syncCards();
  }

  function setQty(variantId, qty) {
    var item = findInBox(variantId);
    if (!item) return;
    if (qty <= 0) {
      state.box = state.box.filter(function (b) {
        return !(b.variantId === variantId && b.boxId === state.boxId);
      });
    } else {
      item.qty = qty;
    }
    renderBox();
    syncCards();
  }

  function renderBox() {
    var items = currentBoxItems();
    var count = items.reduce(function (n, b) { return n + b.qty; }, 0);
    var total = state.box.reduce(function (n, b) { return n + b.price * b.qty; }, 0);
    if (state.card) total += state.card.price;
    var boxes = state.box.reduce(function (m, b) { return Math.max(m, b.boxId); }, 1);

    els.boxLabel.hidden = boxes < 2 && state.boxId === 1;
    els.boxLabel.textContent = 'Box ' + state.boxId;

    els.boxEmpty.hidden = count > 0;
    Array.prototype.slice.call(els.box.querySelectorAll('[data-bb-boxitem]')).forEach(function (n) {
      n.remove();
    });

    items.forEach(function (b) {
      var el = document.createElement('div');
      el.className = 'bb-boxitem';
      el.setAttribute('data-bb-boxitem', '');
      el.innerHTML =
        (b.image ? '<img src="' + b.image + '" alt="">' : '') +
        '<span class="bb-boxitem__qty">' + b.qty + '</span>' +
        '<button type="button" class="bb-boxitem__remove" data-bb-boxremove="' + b.variantId +
        '" aria-label="Remove ' + escapeHtml(b.title) + '">Remove</button>';
      els.box.appendChild(el);
    });

    els.boxCount.textContent = count + (count === 1 ? ' item' : ' items');
    els.boxTotal.textContent = count ? money(total) : '';

    var st = currentStep();
    var isLast = state.stepIndex === CONFIG.steps.length - 1;
    els.next.disabled = state.submitting || !stepSatisfied(state.stepIndex);
    els.next.textContent = state.submitting ? 'Adding…' : (isLast ? 'Add to cart' : 'Next');
    els.back.hidden = state.stepIndex === 0;

    // Another box only makes sense once this one is a complete basket.
    els.addBox.hidden = !(isLast && boxIsComplete(state.boxId));
    if (st.min && boxCountForStep(state.stepIndex) < st.min) {
      els.next.title = 'Pick at least ' + st.min + ' to continue';
    } else {
      els.next.removeAttribute('title');
    }
    renderSteps();
    if (currentStep().type === 'summary') renderSummary();
  }

  /** Reflect box quantities onto whatever cards are currently rendered. */
  function syncCards() {
    Array.prototype.slice.call(els.mount.querySelectorAll('[data-bb-product]')).forEach(function (card) {
      var item = findInBox(card.getAttribute('data-variant-id'));
      card.classList.toggle('is-in-box', !!item);
      var out = card.querySelector('[data-bb-qty-value]');
      if (out && item) out.textContent = item.qty;
    });
  }

  /** A box is complete when every required step has its minimum. */
  function boxIsComplete(boxId) {
    return CONFIG.steps.every(function (st) {
      if (!st.min) return true;
      var n = state.box.reduce(function (acc, b) {
        return b.boxId === boxId && b.step === st.key ? acc + b.qty : acc;
      }, 0);
      return n >= st.min;
    });
  }

  function startAnotherBox() {
    if (!boxIsComplete(state.boxId)) return;
    state.boxId = state.box.reduce(function (m, b) { return Math.max(m, b.boxId); }, 1) + 1;
    goToStep(0);
    setStatus('Started box ' + state.boxId + '. Earlier boxes are saved.');
  }

  // ── Cart ──────────────────────────────────────────────────────────────────
  /**
   * Every line carries `Bundle` and `Box ID`.
   *
   * bb-cart-fee-state.liquid groups on `Bundle` ALONE and ignores `Box ID`, so
   * a multi-box build is deliberately ONE adult signature fee (a1f66d8). Do
   * not "fix" that by making the fee per box without changing the reconciler
   * to match — they have to agree.
   *
   * The fee itself is NOT added here. The reconciler computes it server-side
   * from the real cart, which is the whole reason it can be trusted.
   */
  function buildCartPayload() {
    var msg = {
      to: (els.msgTo.value || '').trim(),
      body: (els.msgBody.value || '').trim(),
      from: (els.msgFrom.value || '').trim()
    };

    /**
     * The message belongs on whatever the recipient actually reads it from:
     * the greeting card if one was picked, otherwise the container. Either
     * way it appears once per basket, never once per bottle.
     */
    var messageHost = state.card ? 'card' : 'basket';

    var lines = state.box.map(function (b) {
      var props = {
        'Bundle': CONFIG.bundleName,
        'Box ID': String(b.boxId)
      };
      if (messageHost === 'basket' && b.step === 'basket' && b.boxId === 1) {
        if (msg.to) props['Gift Message To'] = msg.to;
        if (msg.body) props['Gift Message'] = msg.body;
        if (msg.from) props['Gift Message From'] = msg.from;
      }
      return { id: Number(b.variantId), quantity: b.qty, properties: props };
    });

    if (state.card) {
      var cardProps = {
        'Bundle': CONFIG.bundleName,
        'Box ID': '1',
        'Card': state.card.title
      };
      if (msg.to) cardProps['Gift Message To'] = msg.to;
      if (msg.body) cardProps['Gift Message'] = msg.body;
      if (msg.from) cardProps['Gift Message From'] = msg.from;
      lines.push({ id: Number(state.card.variantId), quantity: 1, properties: cardProps });
    }

    return lines;
  }

  function submitToCart() {
    if (state.submitting) return;
    if (!boxIsComplete(state.boxId)) {
      setStatus('Finish this box before adding to cart.');
      return;
    }
    state.submitting = true;
    renderBox();
    setStatus('Adding your basket to the cart…');

    fetch((window.Shopify && Shopify.routes && Shopify.routes.root || '/') + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: buildCartPayload() })
    })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (e) { throw new Error(e.description || res.status); });
        return res.json();
      })
      .then(function () {
        window.location.href = (window.Shopify && Shopify.routes && Shopify.routes.root || '/') + 'cart';
      })
      .catch(function (err) {
        console.warn(LOG, 'cart add failed', err);
        state.submitting = false;
        renderBox();
        setStatus('Could not add your basket: ' + err.message + '. Nothing was charged — try again.');
        if (window.Sentry && window.Sentry.captureException) {
          try { window.Sentry.captureException(err); } catch (e) {}
        }
      });
  }

  // ── Quick view ────────────────────────────────────────────────────────────
  function openQuickView(card) {
    qvCard = card;
    qvReturnFocus = document.activeElement;
    var handle = card.getAttribute('data-handle');

    els.qv.hidden = false;
    root.querySelectorAll('[data-bb-qv-close]').forEach(function (n) { n.hidden = false; });
    els.qvBody.innerHTML = '<p class="bb-qv__loading">Loading…</p>';
    els.qv.focus();

    var cached = qvCache[handle];
    if (cached) { renderQuickView(cached); return; }

    fetch((window.Shopify && Shopify.routes && Shopify.routes.root || '/') + 'products/' + handle + '.js',
          { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('product fetch ' + res.status);
        return res.json();
      })
      .then(function (data) {
        qvCache[handle] = data;
        if (!els.qv.hidden) renderQuickView(data);
      })
      .catch(function (err) {
        console.warn(LOG, 'quick view failed', err);
        els.qvBody.innerHTML = '<p class="bb-qv__error">Details could not be loaded. ' +
          'You can still add this to your box.</p>';
      });
  }

  function renderQuickView(p) {
    var images = (p.images || []).slice(0, 8);
    var main = images[0] || p.featured_image || '';

    var thumbs = images.length > 1 ? images.map(function (src, i) {
      return '<button type="button" class="bb-qv__thumb" data-bb-qv-thumb="' + escapeHtml(src) +
             '" aria-pressed="' + (i === 0) + '" aria-label="Image ' + (i + 1) + '">' +
             '<img src="' + escapeHtml(src) + '" alt=""></button>';
    }).join('') : '';

    var inBox = qvCard && findInBox(qvCard.getAttribute('data-variant-id'));

    els.qvBody.innerHTML =
      '<div class="bb-qv__gallery">' +
        '<div class="bb-qv__main" data-bb-qv-main>' +
          (main ? '<img src="' + escapeHtml(main) + '" alt="' + escapeHtml(p.title) + '">' : '') +
        '</div>' +
        (thumbs ? '<div class="bb-qv__thumbs">' + thumbs + '</div>' : '') +
      '</div>' +
      '<div class="bb-qv__info">' +
        '<h2 class="bb-qv__title" id="bb-qv-title">' + escapeHtml(p.title) + '</h2>' +
        '<p class="bb-qv__price">' + money(p.price) + '</p>' +
        // Description is the store's own product copy, rendered as authored.
        '<div class="bb-qv__desc">' + (p.description || '') + '</div>' +
        '<button type="button" class="bb-qv__add" data-bb-qv-add' +
          (p.available ? '' : ' disabled') + '>' +
          (p.available ? (inBox ? 'Add another' : 'Add to box') : 'Sold out') +
        '</button>' +
      '</div>';
  }

  function closeQuickView() {
    els.qv.hidden = true;
    root.querySelectorAll('[data-bb-qv-close]').forEach(function (n) { n.hidden = true; });
    els.qvBody.innerHTML = '';
    qvCard = null;
    if (qvReturnFocus && qvReturnFocus.focus) qvReturnFocus.focus();
    qvReturnFocus = null;
  }

  // ── Review step ───────────────────────────────────────────────────────────
  function stepLabel(key) {
    for (var i = 0; i < CONFIG.steps.length; i++) {
      if (CONFIG.steps[i].key === key) return CONFIG.steps[i].name;
    }
    return key;
  }

  function stepIndexOf(key) {
    for (var i = 0; i < CONFIG.steps.length; i++) {
      if (CONFIG.steps[i].key === key) return i;
    }
    return 0;
  }

  function renderSummary() {
    if (!state.box.length) {
      els.summary.innerHTML = '<p class="bb-summary__empty">Your basket is empty. ' +
        'Go back and pick something.</p>';
      return;
    }

    var boxIds = [];
    state.box.forEach(function (b) {
      if (boxIds.indexOf(b.boxId) === -1) boxIds.push(b.boxId);
    });
    boxIds.sort(function (a, b) { return a - b; });

    var html = boxIds.map(function (id) {
      var items = state.box.filter(function (b) { return b.boxId === id; });
      var count = items.reduce(function (n, b) { return n + b.qty; }, 0);

      // Grouped by the step they came from, so the review mirrors the build.
      var byStep = {};
      items.forEach(function (b) { (byStep[b.step] = byStep[b.step] || []).push(b); });

      var groups = CONFIG.steps.filter(function (st) { return byStep[st.key]; }).map(function (st) {
        var rows = byStep[st.key].map(function (b) {
          return '<div class="bb-sumitem">' +
            '<span class="bb-sumitem__media">' +
              (b.image ? '<img src="' + escapeHtml(b.image) + '" alt="">' : '') +
            '</span>' +
            '<span class="bb-sumitem__name">' + escapeHtml(b.title) + '</span>' +
            '<span class="bb-sumqty">' +
              '<button type="button" data-bb-sumqty="-1" data-variant="' + b.variantId + '" data-box="' + id + '" aria-label="Remove one">&minus;</button>' +
              '<output>' + b.qty + '</output>' +
              '<button type="button" data-bb-sumqty="1" data-variant="' + b.variantId + '" data-box="' + id + '" aria-label="Add one">+</button>' +
            '</span>' +
            '<span class="bb-sumitem__price">' + money(b.price * b.qty) + '</span>' +
          '</div>';
        }).join('');

        return '<div class="bb-sumgroup">' +
          '<div class="bb-sumgroup__head">' +
            '<span class="bb-sumgroup__label">' + escapeHtml(st.name) + '</span>' +
            '<button type="button" class="bb-sumgroup__edit" data-bb-sumedit="' + st.key + '" data-box="' + id + '">Edit</button>' +
          '</div>' + rows +
        '</div>';
      }).join('');

      return '<section class="bb-sumbox">' +
        '<div class="bb-sumbox__head">' +
          '<h3 class="bb-sumbox__title">' + (boxIds.length > 1 ? 'Box ' + id : 'Your basket') + '</h3>' +
          '<span class="bb-sumbox__count">' + count + (count === 1 ? ' item' : ' items') + '</span>' +
          (boxIds.length > 1 ? '<button type="button" class="bb-sumbox__remove" data-bb-sumboxremove="' + id + '">Remove box</button>' : '') +
        '</div>' + groups +
      '</section>';
    }).join('');

    // Card and message, if either was set.
    var msgTo = (els.msgTo.value || '').trim();
    var msgBody = (els.msgBody.value || '').trim();
    var msgFrom = (els.msgFrom.value || '').trim();

    if (state.card || msgBody || msgTo) {
      html += '<section class="bb-sumbox">' +
        '<div class="bb-sumbox__head">' +
          '<h3 class="bb-sumbox__title">Greeting card</h3>' +
          '<button type="button" class="bb-sumbox__remove" data-bb-sumedit="message">Edit</button>' +
        '</div>' +
        (state.card ? '<div class="bb-sumitem">' +
          '<span class="bb-sumitem__name">' + escapeHtml(state.card.title) + '</span>' +
          '<span class="bb-sumitem__price">' + (state.card.price ? money(state.card.price) : 'Free') + '</span>' +
        '</div>' : '') +
        ((msgBody || msgTo) ? '<div class="bb-summessage">' +
          (msgTo ? '<strong>To ' + escapeHtml(msgTo) + '</strong>' : '') +
          (msgBody ? '<p class="bb-summessage__body">' + escapeHtml(msgBody) + '</p>' : '') +
          (msgFrom ? '<p class="bb-summessage__body">— ' + escapeHtml(msgFrom) + '</p>' : '') +
        '</div>' : '') +
      '</section>';
    }

    var subtotal = state.box.reduce(function (n, b) { return n + b.price * b.qty; }, 0);
    if (state.card) subtotal += state.card.price;
    var hasAlcohol = state.box.some(function (b) { return b.alcohol; });

    html += '<div class="bb-sumtotals">' +
      '<div class="bb-sumtotals__row"><span>Subtotal</span><span>' + money(subtotal) + '</span></div>' +
      (hasAlcohol ? '<div class="bb-sumtotals__row bb-sumtotals__note">' +
        '<span>Adult signature (required for alcohol)</span><span>' + money(SIGNATURE_FEE) + '</span></div>' : '') +
      '<div class="bb-sumtotals__row bb-sumtotals__total"><span>Total</span><span>' +
        money(subtotal + (hasAlcohol ? SIGNATURE_FEE : 0)) + '</span></div>' +
      '<p class="bb-sumtotals__note">Shipping calculated at checkout.</p>' +
    '</div>';

    els.summary.innerHTML = html;
  }

  function toggleFilter(handle, on) {
    var i = state.filters.indexOf(handle);
    if (on && i === -1) state.filters.push(handle);
    if (!on && i !== -1) state.filters.splice(i, 1);
    state.query = '';
    if (els.search) els.search.value = '';
    updateFilterCount();
    navigate();
  }

  function clearFilters() {
    state.filters = [];
    renderFilters();
    navigate();
  }

  function openDrawer(open) {
    root.classList.toggle('is-filtering', open);
    els.drawer.hidden = !open;
    root.querySelectorAll('[data-bb-filters-close]').forEach(function (n) { n.hidden = !open; });
  }

  function goToStep(index) {
    if (index < 0 || index >= CONFIG.steps.length) return;
    state.stepIndex = index;
    state.filters = [];
    state.query = '';
    if (els.search) els.search.value = '';
    openDrawer(false);

    var type = currentStep().type;
    var isProducts = type === 'products';

    els.message.hidden = type !== 'message';
    els.summary.hidden = type !== 'summary';
    els.mount.hidden = !isProducts;
    els.loadMore.parentNode.hidden = !isProducts;
    els.search.closest('.bb-search').hidden = !isProducts;

    renderSteps();
    renderFilters();
    renderBox();

    if (type === 'message') {
      setStatus('Optional — leave it blank if you would rather not.');
      reset();
      return;
    }
    if (type === 'summary') {
      setStatus('');
      reset();
      renderSummary();
      return;
    }
    navigate();
  }

  // ── Wiring ────────────────────────────────────────────────────────────────
  els.filtersOpen.addEventListener('click', function () { openDrawer(true); });

  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-bb-filters-close]')) openDrawer(false);
  });

  els.filterGroups.addEventListener('click', function (e) {
    var head = e.target.closest('[data-bb-fgroup-toggle]');
    if (head) head.parentNode.classList.toggle('is-open');
  });

  els.filterGroups.addEventListener('change', function (e) {
    var box = e.target.closest('[data-bb-filter]');
    if (box) toggleFilter(box.getAttribute('data-bb-filter'), box.checked);
  });

  root.querySelector('[data-bb-filters-clear]').addEventListener('click', clearFilters);

  els.steps.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-bb-step]');
    if (!btn || btn.disabled) return;
    var keys = CONFIG.steps.map(function (st) { return st.key; });
    goToStep(keys.indexOf(btn.getAttribute('data-bb-step')));
  });

  var searchTimer = null;
  if (els.search) {
    els.search.addEventListener('input', function () {
      clearTimeout(searchTimer);
      var value = els.search.value.trim();
      searchTimer = setTimeout(function () {
        state.query = value;
        navigate();
      }, SEARCH_DEBOUNCE_MS);
    });
  }

  els.loadMore.addEventListener('click', function () { loadMore(); });

  els.mount.addEventListener('click', function (e) {
    var card = e.target.closest('[data-bb-product]');
    if (!card) return;

    if (e.target.closest('[data-bb-view]')) { openQuickView(card); return; }
    if (e.target.closest('[data-bb-add]')) { addToBox(card); return; }

    var step = e.target.closest('[data-bb-qty]');
    if (step) {
      var id = card.getAttribute('data-variant-id');
      var item = findInBox(id);
      setQty(id, (item ? item.qty : 0) + parseInt(step.getAttribute('data-bb-qty'), 10));
    }
  });

  els.summary.addEventListener('click', function (e) {
    var edit = e.target.closest('[data-bb-sumedit]');
    if (edit) {
      var boxAttr = edit.getAttribute('data-box');
      if (boxAttr) state.boxId = Number(boxAttr);
      goToStep(stepIndexOf(edit.getAttribute('data-bb-sumedit')));
      return;
    }

    var q = e.target.closest('[data-bb-sumqty]');
    if (q) {
      // Quantity edits apply to the box the row belongs to, not the box the
      // customer happens to have been filling last.
      var prev = state.boxId;
      state.boxId = Number(q.getAttribute('data-box'));
      var id = q.getAttribute('data-variant');
      var item = findInBox(id);
      setQty(id, (item ? item.qty : 0) + parseInt(q.getAttribute('data-bb-sumqty'), 10));
      state.boxId = prev;
      renderSummary();
      return;
    }

    var rm = e.target.closest('[data-bb-sumboxremove]');
    if (rm) {
      var gone = Number(rm.getAttribute('data-bb-sumboxremove'));
      state.box = state.box.filter(function (b) { return b.boxId !== gone; });
      if (state.boxId === gone) {
        state.boxId = state.box.reduce(function (m, b) { return Math.max(m, b.boxId); }, 1);
      }
      renderBox();
      renderSummary();
    }
  });

  els.box.addEventListener('click', function (e) {
    var rm = e.target.closest('[data-bb-boxremove]');
    if (rm) setQty(rm.getAttribute('data-bb-boxremove'), 0);
  });

  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-bb-qv-close]')) closeQuickView();
  });

  els.qvBody.addEventListener('click', function (e) {
    var thumb = e.target.closest('[data-bb-qv-thumb]');
    if (thumb) {
      var src = thumb.getAttribute('data-bb-qv-thumb');
      var main = els.qvBody.querySelector('[data-bb-qv-main] img');
      if (main) main.src = src;
      els.qvBody.querySelectorAll('[data-bb-qv-thumb]').forEach(function (t) {
        t.setAttribute('aria-pressed', String(t === thumb));
      });
      return;
    }
    if (e.target.closest('[data-bb-qv-add]') && qvCard) {
      addToBox(qvCard);
      closeQuickView();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && !els.qv.hidden) closeQuickView();
  });

  els.next.addEventListener('click', function () {
    if (state.stepIndex < CONFIG.steps.length - 1) {
      goToStep(state.stepIndex + 1);
    } else {
      submitToCart();
    }
  });

  els.addBox.addEventListener('click', startAnotherBox);

  els.back.addEventListener('click', function () {
    if (state.stepIndex > 0) goToStep(state.stepIndex - 1);
  });

  if (els.cards) {
    els.cards.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-bb-card]');
      if (!btn) return;
      var id = btn.getAttribute('data-variant-id');
      var already = state.card && state.card.variantId === id;

      // Tapping the selected card clears it — the step is optional and there
      // needs to be a way back out without reloading.
      state.card = already ? null : {
        variantId: id,
        title: btn.getAttribute('data-title'),
        // Liquid emits variant.price in cents, same as the product cards.
        price: parseInt(btn.getAttribute('data-price'), 10) || 0
      };

      els.cards.querySelectorAll('[data-bb-card]').forEach(function (b) {
        b.setAttribute('aria-pressed', String(!already && b === btn));
      });
      renderBox();

      /**
       * Picking a card is the moment to reveal the message form. The card art
       * is large enough that the fields sit below the fold, and people were
       * not scrolling to find them.
       *
       * Only on SELECT — scrolling away after someone clears their choice
       * would fight them. Focus follows the scroll so keyboard and screen
       * reader users land in the same place, and reduced-motion is honoured
       * because a long smooth scroll is exactly what that setting is for.
       */
      if (!already && els.msgFields) {
        var reduce = window.matchMedia &&
                     window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        try {
          els.msgFields.scrollIntoView({
            behavior: reduce ? 'auto' : 'smooth',
            block: 'start'
          });
        } catch (e) {
          els.msgFields.scrollIntoView();
        }
        // Focus after the scroll settles; focusing first would make the
        // browser jump instantly and cancel the smooth scroll.
        setTimeout(function () {
          if (els.msgTo) els.msgTo.focus({ preventScroll: true });
        }, reduce ? 0 : 350);
      }
    });
  }

  els.msgBody.addEventListener('input', function () {
    els.msgRemaining.textContent = String(400 - els.msgBody.value.length);
  });

  window.__bbBuilder = {
    state: function () { return JSON.parse(JSON.stringify(state)); },
    config: function () { return CONFIG; },
    url: buildUrl,
    goToStep: goToStep,
    box: function () { return state.box.slice(); },
    card: function () { return state.card; },
    payload: buildCartPayload,
    summary: renderSummary,
    quickView: function (handle) {
      var card = els.mount.querySelector('[data-handle="' + handle + '"]');
      if (card) openQuickView(card);
    }
  };

  renderSteps();
  renderFilters();
  renderBox();
  goToStep(0);
})();
